import React from 'react'

import { Button } from '@octane/spark'
import ApplicationStatusIndicator from 'components/ApplicationStatusIndicator'
import FormContent from 'components/FormContent'
import useUser from 'hooks/useUser'

import ApplicationStatusHeaderText from './ApplicationStatusHeaderText'
import {
    ButtonContainer,
    MainContainer,
    StyledHeaderSection,
    Wrapper,
    DateText,
    StatusWrapper,
} from './ReadOnlyApplicationStatus.styled'

function formatDate(dateString: string) {
    const date = new Date(dateString)

    const formattedDate = date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        hour12: true,
    })

    return formattedDate
}

interface CopyingBlocker {
    type: string
    message: string
}

interface RedecisionBlocker {
    type: string
    message: string
}

interface StatusProps {
    id: number
    applicant: string
    status: string
    lender: string
    submittedAt: string
    createdAt: string
    copyingBlocker?: CopyingBlocker | null
    hasActiveContracts?: boolean
    redecisionBlockers?: RedecisionBlocker[]
}

function ReadOnlyApplicationStatus({
    id,
    applicant,
    status,
    lender,
    submittedAt,
    createdAt,
    copyingBlocker,
    hasActiveContracts,
    redecisionBlockers,
}: Readonly<StatusProps>) {
    const { user } = useUser()
    const showStatusButton = ['Approved', 'Declined'].includes(status)

    const statusButtonText =
        showStatusButton && (status === 'Approved' ? 'Work the Deal' : 'Status')

    const headerIdText = `#${id}`

    const headerText = applicant
        ? `${headerIdText}: ${applicant}`
        : headerIdText

    const formattedTimestamp = submittedAt
        ? `Submitted at ${formatDate(submittedAt)}`
        : `Created at ${formatDate(createdAt)}`

    const isDeactivated = user?.dealership?.isDeactivated
    const isCopyBlocked = !!copyingBlocker || isDeactivated
    const isWorkTheDealBlocked = !!hasActiveContracts
    const workTheDealBlockerMessage = redecisionBlockers?.[0]?.message

    return (
        <MainContainer>
            <StyledHeaderSection title="">
                <FormContent gridTemplate="1fr">
                    <Wrapper>
                        <ApplicationStatusHeaderText
                            headerText={headerText}
                            lender={lender}
                        />
                        <StatusWrapper>
                            <ApplicationStatusIndicator status={status} />
                            <DateText>{formattedTimestamp}</DateText>
                        </StatusWrapper>
                    </Wrapper>
                </FormContent>
                <ButtonContainer>
                    <FormContent gridTemplate="1fr 1fr 1fr">
                        <a
                            href={`#/copy-into-new/${id}`}
                            target="_self"
                            rel="noreferrer"
                        >
                            <Button
                                variant="primary"
                                style={{ width: '100%' }}
                                disabled={isCopyBlocked}
                                title={copyingBlocker?.message}
                            >
                                Copy Into New
                            </Button>
                        </a>
                        <Button
                            variant="primaryOutlined"
                            onClick={window.print}
                        >
                            Print
                        </Button>

                        {showStatusButton &&
                            (isWorkTheDealBlocked ? (
                                <Button
                                    variant="primaryOutlined"
                                    style={{ width: '100%' }}
                                    disabled
                                    title={workTheDealBlockerMessage}
                                >
                                    {statusButtonText}
                                </Button>
                            ) : (
                                <a
                                    href={`#/application/status/${id}`}
                                    target="_self"
                                    rel="noreferrer"
                                >
                                    <Button
                                        variant="primaryOutlined"
                                        style={{ width: '100%' }}
                                    >
                                        {statusButtonText}
                                    </Button>
                                </a>
                            ))}
                    </FormContent>
                </ButtonContainer>
            </StyledHeaderSection>
        </MainContainer>
    )
}

export default ReadOnlyApplicationStatus
